import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, isAdmin } from '@/lib/auth-context';

/**
 * Global keyboard shortcuts (active outside input/textarea/select).
 *
 * | Key        | Action                                        |
 * |------------|-----------------------------------------------|
 * | Ctrl+K / ⌘K | Focus the search input                       |
 * | g          | Open deck switcher                            |
 * | l          | Open card lookup overlay                      |
 * | f          | Go to My Decks and open Find a Home           |
 * | m          | Go to My Decks (requires login)               |
 * | p          | Go to your profile (requires login)           |
 * | a          | Go to Admin panel (admin only)                |
 * | ?          | Open keyboard shortcuts help overlay          |
 * | Escape     | Dispatch 'close-dialogs' custom event         |
 */
export function useKeyboardShortcuts() {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      // Ctrl+K or Cmd+K — focus search input
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        const searchInput =
          document.getElementById('search-input') ??
          document.querySelector<HTMLInputElement>('input[type="search"]:not([hidden])');
        if (searchInput && searchInput instanceof HTMLElement) {
          searchInput.focus();
        }
        return;
      }

      // Escape — dispatch close-dialogs event
      if (e.key === 'Escape') {
        window.dispatchEvent(new CustomEvent('close-dialogs'));
        return;
      }

      // Single-key shortcuts — only fire when not typing in an editable element
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const tag = (document.activeElement as HTMLElement)?.tagName?.toLowerCase();
      const isEditable =
        tag === 'input' || tag === 'textarea' || tag === 'select' ||
        (document.activeElement as HTMLElement)?.isContentEditable;
      if (isEditable) return;

      switch (e.key) {
        case 'g':
          e.preventDefault();
          window.dispatchEvent(new CustomEvent('open-deck-switcher'));
          break;

        case 'l':
          e.preventDefault();
          window.dispatchEvent(new CustomEvent('open-card-lookup'));
          break;

        case 'f':
          if (user) {
            e.preventDefault();
            navigate('/my-decks', { state: { openFindHome: true } });
          }
          break;

        case 'm':
          if (user) {
            e.preventDefault();
            navigate('/my-decks');
          }
          break;

        case 'p': {
          const activePlaytest = localStorage.getItem('active-playtest-url');
          if (activePlaytest && window.location.pathname !== activePlaytest) {
            e.preventDefault();
            navigate(activePlaytest);
          } else if (user && !activePlaytest) {
            e.preventDefault();
            navigate(`/user/${user.id}`);
          }
          break;
        }

        case 'a':
          if (user && isAdmin(user)) {
            e.preventDefault();
            navigate('/admin');
          }
          break;

        case '?':
          e.preventDefault();
          window.dispatchEvent(new CustomEvent('open-keyboard-help'));
          break;
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [navigate, user]);
}
